import { useCallback } from 'react';
import { usePlatform } from '@localagent/platform';

const MIME: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  svg: 'image/svg+xml',
  zip: 'application/zip',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

function extOf(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot >= 0 ? filename.slice(dot + 1).toLowerCase() : '';
}

function mimeFor(filename: string): string {
  return MIME[extOf(filename)] ?? 'application/octet-stream';
}

// Extensions safe to hand to the OS opener on desktop:
// the office set plus genuinely inert document formats. Everything else —
// `.command`, `.sh`, `.jar`, `.webloc`, unknown — keeps the download behavior,
// because workspace files are untrusted (agent-written, repo-sourced) and
// macOS `shell.openPath` would EXECUTE several of those types.
const OS_OPEN_ALLOWLIST = new Set(['docx', 'xlsx', 'xls', 'ods', 'pptx', 'ppt', 'doc', 'rtf', 'csv']);

interface OpenExternallyBridge {
  openFileExternally?: (filename: string, bytes: Uint8Array) => Promise<void>;
}

export function useOpenExternally() {
  const platform = usePlatform();

  const openExternally = useCallback(
    async (filename: string, bytes: Uint8Array) => {
      const base = filename.split('/').pop() || filename;

      if (platform.type === 'desktop' && OS_OPEN_ALLOWLIST.has(extOf(base))) {
        const api = (window as unknown as { electronAPI?: OpenExternallyBridge }).electronAPI;
        if (!api?.openFileExternally) {
          throw new Error(
            'window.electronAPI.openFileExternally is unavailable — the Electron preload bridge did not load',
          );
        }
        await api.openFileExternally(base, bytes);
        return;
      }

      const buf = bytes.slice().buffer;
      const url = URL.createObjectURL(new Blob([buf], { type: mimeFor(base) }));
      const a = document.createElement('a');
      a.href = url;
      a.download = base;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    },
    [platform.type],
  );

  return {
    openExternally,
    platformType: platform.type,
  };
}
